import { type MouseEvent, useCallback } from 'react'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { ProductItem } from '@/components/SelectInsuranceGrid/ProductItem'
import { type ProductFragment } from '@/services/graphql/generated'
import { Heading } from 'ui/src/components/Heading/Heading'
import { grid, LAYOUT, title } from './SelectInsuranceGrid.css'

const ITEM_THRESHOLD = 4

type Props = {
  products: Array<ProductFragment>
  heading?: string
  openPriceCalculator?: boolean
  className?: string
  onProductClick?: (href: string) => void
}

export function SelectInsuranceGrid({
  className,
  heading,
  products,
  openPriceCalculator = true,
  onProductClick,
  ...others
}: Props) {
  const handleProductClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      const pathname = event.currentTarget.pathname
      if (pathname) onProductClick?.(pathname)
    },
    [onProductClick],
  )

  return (
    <GridLayout.Root className={className} {...others}>
      <GridLayout.Content width="1" align="center">
        {heading && (
          <Heading as="h2" className={title} variant={{ _: 'serif.32', lg: 'serif.48' }} align="center">
            {heading}
          </Heading>
        )}

        <div className={grid} data-layout={products.length > ITEM_THRESHOLD ? LAYOUT.GRID : LAYOUT.COLUMN}>
          {products.map((product) => {
            const href = openPriceCalculator ? product.priceCalculatorPageLink : product.pageLink
            return (
              <ProductItem.Root key={product.id}>
                <ProductItem.Pillow {...product.pillowImage} />
                <ProductItem.Content>
                  <ProductItem.TitleLink href={href} title={product.displayNameFull} onClick={handleProductClick}>
                    {product.displayNameShort}
                  </ProductItem.TitleLink>
                  <ProductItem.Tagline>{product.tagline}</ProductItem.Tagline>
                </ProductItem.Content>
              </ProductItem.Root>
            )
          })}
        </div>
      </GridLayout.Content>
    </GridLayout.Root>
  )
}
